<template>
    <modal :id="utils.randomId({prefix: 'modal'})"
           :show="true"
           @close="close()"
           size="lg">
        <template #title>{{ element ? 'Update' : 'Create new' }} {{ type_title }}</template>


        <div v-if="is_busy" class="busy"><icon-spinner :spin="true" color="dark" size="24"/></div>

        <form v-else action="" @submit.prevent.stop="saveElement()">

            <div class="form-group">
                <label for="element-name">{{ type_title }} Name <span class="required">*</span></label>
                <input id="element-name"
                       class="form-control form-control-lg"
                       v-model="el.name"
                       :placeholder="`${type_title} name`">
                <span class="text-danger" v-if="error.errors['name']">
                    {{ error.errors['name'].join(', ') }}
                </span>
            </div>

            <div class="form-group">
                <label>Show this {{ type_title }} in <span class="required">*</span></label>
                <div class="checkboxes">
                    <div class="checkbox" v-for="stage in stages" :key="stage.id">
                        <image-check v-model="el.groups" :value="stage.id"
                                     :image="stage.meta?.icon">
                            {{ stage.description }}
                        </image-check>
                    </div>
                </div>
                <span class="text-danger" v-if="error.errors['groups']">
                    {{ error.errors['groups'].join(', ') }}
                </span>
            </div>

            <div class="form-group">
                <label for="element-description">Description <span class="required">*</span></label>
                <textarea id="element-description"
                          class="form-control"
                          rows="8"
                          v-model="el.data.description"
                          :placeholder="`${type_title} Content (Markdown Supported)`"></textarea>
                <span class="text-danger" v-if="error.errors['data.description']">
                    {{ error.errors['data.description'].join(', ') }}
                </span>
            </div>

            <div class="form-group">
                <label>{{ type_title }} Type</label>
                <div class="checkboxes upload-type">
                    <div class="checkbox">
                        <image-check
                            v-model="el.data.type"
                            title="External Link"
                            value="link"
                            image="/images/canvas-element/ce-type-link.png"
                            type="radio"/>
                    </div>
                    <div class="checkbox">
                        <image-check
                            v-model="el.data.type"
                            title="Upload File"
                            value="file"
                            image="/images/canvas-element/ce-type-file.png"
                            type="radio"/>
                    </div>
                </div>
                <span class="text-danger" v-if="error.errors['data.type']">
                    {{ error.errors['data.type'].join(', ') }}
                </span>
            </div>

            <div class="form-group" v-if="el.data.type === 'file'">
                <label>Upload Image / Document (.jpg, .png, .bmp, .pdf, .doc, .docx, .ppt, .pptx, .xls, .xslx)</label>
                <file-uploader v-model="el.data.file" storage-path="public-uploaded-elements"/>
                <span class="text-danger" v-if="error.errors['data.file']">
                    {{ error.errors['data.file'].join(', ') }}
                </span>
            </div>
            <div class="form-group" v-if="el.data.type === 'link'">
                <label>Link to page</label>
                <input type="url" v-model="el.data.url" placeholder="URL" class="form-control"/>
                <span class="text-danger" v-if="error.errors['data.url']">
                    {{ error.errors['data.url'].join(', ') }}
                </span>
            </div>

            <div class="form-group">
                <label>Preview Image (.jpg, .png, .bmp)</label>
                <file-uploader v-model="el.data.preview_file" storage-path="public-uploaded-preview"/>
                <span class="text-danger" v-if="error.errors['data.preview_file']">
                    {{ error.errors['data.preview_file'].join(', ') }}
                </span>
            </div>

            <div class="form-group">
                <label>Width <span class="required">*</span></label>
                <div class="checkboxes">
                    <div class="checkbox" v-for="i in 5">
                        <image-check
                            v-model="el.data.options.width"
                            :value="i"
                            :image="`/images/activity-widths/w${i}.svg`"
                            type="radio"/>
                    </div>
                </div>
                <span class="text-danger" v-if="error.errors['data.options.width']">
                    {{ error.errors['data.options.width'].join(', ') }}
                </span>
            </div>

        </form>


        <div class="alert alert-danger" v-if="error.message">
            {{ error.message }}
        </div>

        <template #footer>
            <button class="btn btn-warning mr-auto" @click="close()">Cancel</button>
            <state-button class="btn btn-success ml-auto"
                          :state="is_saving ? 'busy' : ''"
                          :disabled="is_saving"
                          @click="saveElement()">{{ element ? 'Save' : 'Create' }} {{ type_title }}</state-button>
        </template>
    </modal>
</template>

<script>
import utils from "@/mixins/utils";
import StateButton from "@/Components/Common/StateButton";
import ImageCheck from "@/Components/Common/ImageCheck";
import FileUploader from "@/Components/Common/FileUploader";
import Modal from "@/Components/Common/Modal";
import IconSpinner from "@/Icons/IconSpinner";
import API from "@/API";
import ErrorLine from "@/Components/Common/ErrorLine";

export default {
    name: "CanvasElementEditorModal",
    components: {ErrorLine, IconSpinner, Modal, FileUploader, ImageCheck, StateButton},
    mixins: [utils],
    props: ['element', 'type'],


    data(){
        return {
            is_busy: true,
            is_saving: false,

            el: null,
            error: {
                errors: [],
                message: '',
            },

        };
    },

    computed: {
        type_title(){
            return this.utils.ucwords(this.type);
        },
    },

    methods: {


        close(){
            this.$emit('close');
        },


        clearErrors(){
            this.error.errors = [];
            this.error.message = '';
        },

        async saveElement(){

            this.clearErrors();

            if( this.element ){
                await this.createElement();
            }else{
                await this.updateElement();
            }
        },

        async updateElement(){
            this.is_saving = true;
            try{
                const element = await API.elements.store(this.el);
                this.onElementSaved(element);
            }catch (e) {
                this.error = e.response.data;
                console.log({error: e});
            }
            this.is_saving = false;
        },

        async createElement(){
            this.is_saving = true;

            try{
                const element = await API.elements.update(this.el.id, this.el);
                this.onElementSaved(element);
            }catch (e) {
                this.error = e.response.data;
                console.log({error: e});
            }

            this.is_saving = false;
        },

        onElementSaved(element){
            this.$emit('saved', element);
            this.close();
        },

        async loadStages(){
            this.is_busy    = true;
            this.stages     = await API.groups.bulk({type: 'stage'});
            this.is_busy    = false;
        },

    },

    async beforeMount() {

        if(this.element){
            const el = _.clone(this.element);
            el.groups = el.groups.map(g => g.id);
            this.el = el;

        }else{
            if( this.type === 'resource' ){
                this.el = {
                    name: `Untitled Resource`,
                    type: this.type,
                    groups: [],
                    data: {
                        options: {width: 1},
                        description: '',
                        type: 'link',
                        url: '',
                        file: null,
                        preview_file: null,
                    },
                };
            }else if (this.type === 'activity'){
                this.el = {
                    name: `Untitled Activity`,
                    type: this.type,
                    groups: [],
                    data: {
                        options: {width: 1},
                        description: '',
                        uploadable: 'document',
                        type: 'link',
                        url: '',
                        file: null,
                        preview_file: null,
                    },
                };
            }
        }

        console.log({element: this.el});


        await this.loadStages();

    },

}
</script>

<style lang="scss" scoped>

.checkboxes{
    display: flex;
    align-items: stretch;
    margin-left: -.3rem;
    margin-right: -.3rem;
    margin-bottom: 1rem;

    .checkbox {
        display: flex;
        width: 100%;
        flex: 1 1 auto;
        padding-left: .3rem;
        padding-right: .3rem;
    }
}

.busy{
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6rem 1rem;
}
.upload-type{
    > * {
        max-width: 135px;
        padding: .3rem;
    }
}
.required{
    color: $danger;
}
</style>
